import { useEffect, useMemo, useRef, useState } from "react";
import { api, Manifest, takeGraphFocus } from "../api";
import { signedUsd } from "../lib/money";
import GraphCanvas, { GEdge, GNode, LANES, ShapeSwatch, Sub, TYPE, laneOf } from "./GraphCanvas";
import GraphRail from "./GraphRail";
import OlderToggle from "./OlderToggle";
import Bedrock from "./Bedrock";
import TradeStory from "./TradeStory";
import { MoneyBadge, readMoney } from "./MoneyScope";

type GraphSummary = {
  nodes: Record<string, number>;
  edges: Record<string, number>;
  total_nodes: number;
  total_edges: number;
  remote: string;
  sync: { unsynced_nodes: number; unsynced_edges: number; last_sync_at: string | null };
  platform_access: { actor: string; event: string | null; ts: string }[];
};

type NodeRef = { rel: string; id: string; type: string; label: string };
// What a node CAUSED, in money — its downstream consequence set. The fan is
// one-to-many (a session issues many directives, a directive is cited by many
// proposals, one order can close many lots), which is precisely the branching
// a single trade's story can't show.
type ImpactTrade = {
  trade_key: string | null; symbol: string; side: string; qty: number;
  pnl: number; book: string; money?: "real" | "paper"; exit_time: string | null; junior: string | null; result: string;
};
type Impact = {
  applicable: boolean; scope?: string; note?: string;
  // A node's fan is structurally one book (a directive/session/order/proposal
  // can't span books) — money is real vs. paper for the WHOLE panel, not
  // per-row, so it's shown once, prominently (Alan, 2026-08-06: "hard to
  // tell whether commentary is about the live or the paper book").
  money?: "real" | "paper";
  trades: number; net_pnl: number; wins: number; losses: number;
  best?: ImpactTrade; worst?: ImpactTrade; trades_list: ImpactTrade[];
};
type Detail = {
  found: boolean; id: string; type: string; label: string; domain_key: string | null;
  properties: Record<string, unknown>; created_at: string | null; synced: boolean;
  out: NodeRef[]; in: NodeRef[]; impact?: Impact;
};
type Fock = {
  configured: boolean; api_url?: string; atlas_url?: string;
  identity_set?: boolean; synced_nodes?: number; pending?: number;
};
type Verify = { ok: boolean; read_back_nodes?: number; read_back_edges?: number; sample?: string[]; error?: string };
type Access = { atlas_url?: string; api_url?: string; visitor_cookie_name?: string; visitor_token?: string; vault_passphrase?: string };
type SearchHit = { id: string; type: string; label: string; domain_key: string | null; snippet?: string };
type PathResult = {
  found: boolean; nodes?: GNode[]; edges?: GEdge[]; mode?: string;
  also_connected?: { hops: number; note: string };
};
type EgoEdge = { src: string; rel: string; dst: string; gap_hours: number | null };
type Ego = {
  found: boolean; root: GNode; nodes: (GNode & { hops: number })[];
  edges: EgoEdge[]; dropped: number;
};
type Spine = {
  days: number; cutoff: string; nodes: GNode[]; edges: GEdge[];
  older_nodes: number; truncated: boolean;
};

export default function GraphExplorer(_: { manifest: Manifest }) {
  const [summary, setSummary] = useState<GraphSummary | null>(null);
  const [sub, setSub] = useState<Sub | null>(null);
  const [fock, setFock] = useState<Fock | null>(null);
  const [verify, setVerify] = useState<Verify | null>(null);
  const [access, setAccess] = useState<Access | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [hover, setHover] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [ego, setEgo] = useState<Ego | null>(null);
  const [error, setError] = useState<string | null>(null);
  const loaded = useRef(false);

  // Type filter — which node types are visible in the canvas. Defaults to
  // "all present"; toggled by clicking a legend chip.
  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(new Set());

  // The window. The explorer opens on the last 5 BUSINESS days rather than
  // "the 80 newest edges", which was 2% of the graph and — because the newest
  // edges are always this morning's authored_by/filed_in burst — the least
  // informative 2%. "Show older" widens to 20 business days; past that,
  // search is the honest tool and the UI says so.
  const [days, setDays] = useState(5);
  const [spine, setSpine] = useState<Spine | null>(null);

  // Search box — jump to a node by label/domain_key.
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<SearchHit[] | null>(null);
  const [searching, setSearching] = useState(false);

  // Trace-path mode — click a start node, then an end node.
  const [tracing, setTracing] = useState(false);
  const [pathFrom, setPathFrom] = useState<SearchHit | GNode | null>(null);
  const [pathResult, setPathResult] = useState<PathResult | null>(null);
  const [pathError, setPathError] = useState<string | null>(null);

  // Trade Story hand-off — an Order node's domain_key IS an order id, and
  // performance.trade_detail now resolves either the entry or exit id to
  // the same trade, so this works no matter which leg got clicked.
  const [storyOrderId, setStoryOrderId] = useState<string | null>(null);

  async function openNode(id: string) {
    setSelected(id);
    setDetail(null);
    setEgo(null);
    try {
      const d = await api.get<Detail>(`/api/graph/node/${id}`);
      if (d.found) setDetail(d);
    } catch { /* leave detail null */ }
    // Ancestry + consequences, 2 hops each way, in one call — the flat in/out
    // lists below only ever showed ONE hop, so a two-step chain (order →
    // proposal → directive) had to be walked by clicking.
    try {
      const e = await api.get<Ego>(`/api/graph/ego/${id}?up=2&down=2`);
      if (e.found) setEgo(e);
    } catch { /* the flat lists still render */ }
  }

  async function runTracePath(toId: string) {
    if (!pathFrom) return;
    setPathError(null);
    try {
      const r = await api.get<PathResult>(
        `/api/graph/path?from=${encodeURIComponent(pathFrom.id)}&to=${encodeURIComponent(toId)}`,
      );
      setPathResult(r);
      if (!r.found) setPathError("No chain found between those two within 8 hops.");
    } catch (e) {
      setPathError((e as Error).message);
    }
  }

  function handleCanvasSelect(id: string) {
    if (tracing) {
      if (!pathFrom) {
        const n = sub?.nodes.find((x) => x.id === id);
        if (n) { setPathFrom(n); setPathResult(null); setPathError(null); }
      } else {
        runTracePath(id);
      }
      return;
    }
    openNode(id);
  }

  function clearTrace() {
    setTracing(false);
    setPathFrom(null);
    setPathResult(null);
    setPathError(null);
  }

  // The window is its own fetch, so changing it doesn't re-run the one-shot
  // mount work (or re-claim a Trade Story hand-off that was already claimed).
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const s = await api.get<Spine>(`/api/graph/spine?days=${days}`);
        if (!live) return;
        setSpine(s);
        setSub({ nodes: s.nodes, edges: s.edges });
      } catch (e) { if (live) setError((e as Error).message); }
    })();
    return () => { live = false; };
  }, [days]);

  useEffect(() => {
    (async () => {
      try {
        setSummary(await api.get<GraphSummary>("/api/graph/summary"));
        api.get<Fock>("/api/graph/fockspace").then(setFock).catch(() => {});
        loaded.current = true;
        // A Trade Story's "View full graph" hand-off — claim it once, render
        // it the exact same way a traced path renders (visibleSub/
        // pathHighlight below are already built for {found, nodes, edges}).
        const focusIds = takeGraphFocus();
        if (focusIds && focusIds.length > 0) {
          const r = await api.get<{ nodes: GNode[]; edges: GEdge[] }>(
            `/api/graph/nodes?ids=${focusIds.join(",")}`);
          setPathResult({ found: r.nodes.length > 0, nodes: r.nodes, edges: r.edges });
          setTracing(true); // reuse trace-mode's status line + "clear" button, not a new UI state
        }
      } catch (e) { setError((e as Error).message); }
    })();
    // 120s, not 20s. The graph is a record, not a tape — nothing here is
    // acted on in the seconds after it lands, and the canvas itself doesn't
    // re-render on this tick anyway, so a fast poll only ever made the badge
    // counts disagree with the drawing sooner.
    const iv = setInterval(async () => {
      try { setSummary(await api.get<GraphSummary>("/api/graph/summary")); } catch { /**/ }
    }, 120000);
    return () => clearInterval(iv);
  }, []);

  // Debounced search-as-you-type.
  useEffect(() => {
    if (!query.trim()) { setHits(null); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        setHits(await api.get<SearchHit[]>(`/api/graph/search?q=${encodeURIComponent(query)}&limit=15`));
      } catch {
        setHits([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const typesPresent = useMemo(
    () => [...new Set((sub?.nodes ?? []).map((n) => n.type))].filter((t) => TYPE[t]), [sub]);

  // Types grouped by lane, with counts from THIS window — the old chips
  // showed tenant-wide totals next to a canvas holding a tiny slice, so the
  // numbers described something the user could not see.
  const laneGroups = useMemo(() => {
    const counts = new Map<string, number>();
    for (const n of sub?.nodes ?? []) counts.set(n.type, (counts.get(n.type) ?? 0) + 1);
    return LANES.map((ln, i) => ({
      ...ln,
      index: i,
      types: typesPresent.filter((t) => laneOf(t) === i)
        .sort((a, b) => (counts.get(b) ?? 0) - (counts.get(a) ?? 0)),
      count: [...counts].filter(([t]) => laneOf(t) === i).reduce((s, [, c]) => s + c, 0),
    })).filter((g) => g.types.length > 0);
  }, [sub, typesPresent]);

  function toggleLane(g: { types: string[] }) {
    const allHidden = g.types.every((t) => hiddenTypes.has(t));
    setHiddenTypes((prev) => {
      const next = new Set(prev);
      for (const t of g.types) { if (allHidden) next.delete(t); else next.add(t); }
      return next;
    });
  }

  // What the canvas actually renders: the recent subgraph, filtered by
  // hidden types, OR the traced path when one's been found.
  const visibleSub: Sub = useMemo(() => {
    if (pathResult?.found && pathResult.nodes && pathResult.edges) {
      return { nodes: pathResult.nodes, edges: pathResult.edges };
    }
    if (!sub) return { nodes: [], edges: [] };
    if (hiddenTypes.size === 0) return sub;
    const keep = new Set(sub.nodes.filter((n) => !hiddenTypes.has(n.type)).map((n) => n.id));
    return {
      nodes: sub.nodes.filter((n) => keep.has(n.id)),
      edges: sub.edges.filter((e) => keep.has(e.src) && keep.has(e.dst)),
    };
  }, [sub, hiddenTypes, pathResult]);

  const pathHighlight = useMemo(() => {
    if (!pathResult?.found || !pathResult.nodes) return undefined;
    return new Set(pathResult.nodes.map((n) => n.id));
  }, [pathResult]);
  const pathEdgeKeys = useMemo(() => {
    if (!pathResult?.found || !pathResult.edges) return undefined;
    return new Set(pathResult.edges.map((e) => `${e.src}>${e.rel}>${e.dst}`));
  }, [pathResult]);

  function toggleType(t: string) {
    setHiddenTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t); else next.add(t);
      return next;
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">Graph Explorer</h1>
        {summary && (
          <span className={`badge ${summary.remote === "fockspace" ? "bg-up/20 text-up" : "bg-slate-700 text-slate-300"}`}>
            {summary.remote === "fockspace" ? "mirroring to fock.space" : "local graph"} · {summary.total_nodes} nodes / {summary.total_edges} links
          </span>
        )}
      </div>
      <p className="text-sm text-slate-400">
        The firm&rsquo;s institutional memory as a living map — every decision wired to what caused it.
        Follow an arrow and a trade walks all the way back to the charter it served. Hover a node to trace it,
        click to open its full detail. This content is yours; the platform sees counts only.
      </p>
      {error && <div className="text-down text-sm">{error}</div>}

      {/* Search + trace-path controls */}
      <div className="card space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search nodes by name…"
              className="input w-full text-sm"
            />
            {query.trim() && (
              <div className="absolute z-10 mt-1 w-full card border-edge shadow-lg max-h-64 overflow-auto p-1">
                {searching && <div className="text-xs text-slate-500 p-2">searching…</div>}
                {!searching && hits?.length === 0 && (
                  <div className="text-xs text-slate-500 p-2">no matches</div>
                )}
                {hits?.map((h) => (
                  <button
                    key={h.id}
                    className="block text-xs text-left w-full py-1 px-1.5 rounded hover:bg-slate-800"
                    onClick={() => {
                      if (tracing && !pathFrom) { setPathFrom(h); setPathResult(null); setPathError(null); }
                      else if (tracing && pathFrom) { runTracePath(h.id); }
                      else { openNode(h.id); }
                      setQuery("");
                      setHits(null);
                    }}
                  >
                    <span className="flex items-center gap-1.5 w-full">
                      <ShapeSwatch type={h.type} size={8} />
                      <span className="text-slate-300 truncate">{h.label}</span>
                      <span className="text-slate-600 ml-auto shrink-0">{h.type}</span>
                    </span>
                    {/* Search now matches the node's full property text, so a
                        hit can live 3,000 characters into a perspective. The
                        snippet shows WHERE it matched — without it the row's
                        label doesn't contain the search term and reads as a
                        bug. */}
                    {h.snippet && (
                      <span className="block w-full text-[10px] text-slate-500 pl-3.5 truncate">
                        {h.snippet}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            className={`btn text-xs ${tracing ? "btn-primary" : ""}`}
            onClick={() => (tracing ? clearTrace() : (setTracing(true), setPathFrom(null), setPathResult(null)))}
          >
            {tracing ? "Cancel trace" : "Trace path"}
          </button>
        </div>
        {tracing && (
          <div className="text-xs text-slate-400 flex items-center gap-2 flex-wrap">
            {!pathFrom && !pathResult && <span>Click (or search) a <b className="text-slate-200">start</b> node…</span>}
            {pathFrom && !pathResult && (
              <span>From <b className="text-gold">{pathFrom.label}</b> — now click (or search) an <b className="text-slate-200">end</b> node…</span>
            )}
            {pathResult?.found && pathResult.nodes && (
              <span className="text-up">
                ✓ {pathResult.nodes.length} node chain, {pathResult.edges?.length ?? 0} hops
                {pathResult.mode === "provenance" ? " (most explanatory route)" : ""}.
              </span>
            )}
            {/* The road not taken. A provenance trace deliberately refuses to
                route through hubs, so it can be longer than the raw hop-count
                answer. Saying so beats silently withholding the other route. */}
            {pathResult?.also_connected && (
              <span className="text-slate-500 w-full">↳ {pathResult.also_connected.note}</span>
            )}
            {pathError && <span className="text-down">{pathError}</span>}
            <button className="btn text-xs ml-auto" onClick={clearTrace}>clear</button>
          </div>
        )}
      </div>

      <div className="card p-0 overflow-hidden">
        {sub ? (
          <>
            {/* Desktop: the lane canvas. Phone: the story rail — a different
                projection, not a shrunken SVG (see GraphRail's docstring). */}
            <div className="hidden md:block">
              <GraphCanvas
                sub={visibleSub}
                hover={hover}
                onHover={setHover}
                selected={selected}
                onSelect={handleCanvasSelect}
                highlightIds={pathHighlight}
                pathEdgeKeys={pathEdgeKeys}
                layoutMode={pathResult?.found ? "force" : "lane"}
              />
            </div>
            <div className="md:hidden">
              <GraphRail sub={visibleSub} selected={selected} onSelect={handleCanvasSelect} />
            </div>
          </>
        ) : (
          <div className="p-8 text-slate-500 text-sm text-center">loading…</div>
        )}
        {spine && !pathResult?.found && (
          <div className="border-t border-edge px-3">
            <OlderToggle
              count={spine.older_nodes}
              open={days > 5}
              noun={`older node${spine.older_nodes === 1 ? "" : "s"}`}
              onToggle={() => setDays(days > 5 ? 5 : 20)}
            />
            {days > 5 && (
              <div className="text-[11px] text-slate-600 pb-2 font-sans">
                Showing 20 business days. Beyond that the honest tool is search — drawing
                {" "}{summary?.total_nodes ?? "all"} nodes at once has no reading anyone acts on.
              </div>
            )}
            {spine.truncated && (
              <div className="text-[11px] text-gold pb-2 font-sans">
                Window capped — some nodes in this range aren&rsquo;t drawn. Narrow the window or search.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Node inspector — click-through detail */}
      {selected && !tracing && (
        <div className="card border-gold/40 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <ShapeSwatch type={detail?.type ?? ""} size={12} />
              <div>
                <div className="font-bold text-slate-100">{detail?.label ?? "loading…"}</div>
                <div className="text-xs text-slate-500">
                  {detail?.type}{detail?.type && TYPE[detail.type] ? ` — ${TYPE[detail.type].what}` : ""}
                </div>
              </div>
            </div>
            <button className="btn text-xs" onClick={() => { setSelected(null); setDetail(null); }}>close</button>
          </div>

          {detail && (
            <>
              {detail.type === "Order" && detail.domain_key && (
                <button className="btn btn-primary text-xs"
                        onClick={() => setStoryOrderId(detail.domain_key)}>
                  Full trade story →
                </button>
              )}
              <ImpactPanel impact={detail.impact} onOpenStory={setStoryOrderId} />
              {Object.keys(detail.properties).length > 0 && (
                <div>
                  <h3 className="label">Properties</h3>
                  <table className="w-full text-xs">
                    <tbody>
                      {Object.entries(detail.properties).map(([k, v]) => (
                        <tr key={k} className="border-t border-edge align-top">
                          <td className="py-1 pr-3 text-slate-500 font-mono whitespace-nowrap">{k}</td>
                          <td className="py-1 text-slate-300 break-words">
                            {typeof v === "object" ? JSON.stringify(v) : String(v)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-3">
                <EgoSide title="What authorized this" ego={ego} rootId={detail.id}
                         direction="up" flat={detail.in} onOpen={openNode} />
                <EgoSide title="What it caused" ego={ego} rootId={detail.id}
                         direction="down" flat={detail.out} onOpen={openNode} />
              </div>
              {ego && ego.dropped > 0 && (
                <div className="text-[11px] text-slate-600">
                  {ego.dropped} further connection{ego.dropped === 1 ? "" : "s"} not expanded —
                  this node sits on a busy hub.
                </div>
              )}

              <div className="flex flex-wrap gap-4 text-xs text-slate-600 border-t border-edge pt-2">
                {detail.created_at && <span>created {new Date(detail.created_at).toLocaleString()}</span>}
                <span>{detail.synced ? "✓ mirrored to fock.space" : "pending mirror"}</span>
                {detail.domain_key && <span className="font-mono">key: {detail.domain_key.slice(0, 32)}</span>}
              </div>
            </>
          )}
          {!detail && <div className="text-xs text-slate-500">loading detail…</div>}
        </div>
      )}

      {/* Legend, grouped by lane. Nineteen flat type chips was a wall; five
          lanes with the types nested under them is the same hierarchy the
          canvas draws, and the counts are for THIS window, not the tenant. */}
      <div className="card">
        <h2 className="label">
          In this window <span className="text-slate-600 font-normal">(click a lane or a type to filter)</span>
        </h2>
        <div className="space-y-2">
          {laneGroups.map((g) => {
            const laneHidden = g.types.every((t) => hiddenTypes.has(t));
            return (
              <div key={g.key} className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <button onClick={() => toggleLane(g)}
                        className={`text-xs font-mono w-28 text-left shrink-0 ${
                          laneHidden ? "text-slate-600" : "text-slate-300"}`}
                        style={{ minHeight: 32 }}
                        title={g.hint}>
                  {g.label} <span className="text-slate-600">×{g.count}</span>
                </button>
                {g.types.map((t) => {
                  const hidden = hiddenTypes.has(t);
                  return (
                    <button key={t} onClick={() => toggleType(t)}
                      className={`flex items-center gap-1.5 text-xs ${hidden ? "opacity-35" : ""}`}
                      style={{ minHeight: 32 }}>
                      <ShapeSwatch type={t} size={10} />
                      <span className="text-slate-300">{t}</span>
                      <span className="text-slate-500 hidden sm:inline">— {TYPE[t]?.what}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
          {laneGroups.length === 0 && (
            <span className="text-slate-600 text-sm">nothing in this window — widen it, or search</span>
          )}
        </div>
      </div>

      {/* Bedrock — canon, in its own band beneath the firm's lanes. Universe →
          neighborhood → dark matter, navigable; drawn at canon's own x/y. */}
      {fock?.configured && <Bedrock />}

      {/* fock.space connectivity — the broader graph + the private overlay */}
      {fock?.configured && (
        <div className="card border-accent/30 space-y-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="label mb-0">On fock.space</h2>
            <a href={fock.atlas_url} target="_blank" rel="noreferrer"
               className="btn btn-primary text-xs">Open fock.space ↗</a>
          </div>
          {/* This used to claim the encrypted mirror "connects to the broader
              shared universe". It cannot, and never could: fock.space's own
              charter (Art. II §4) says sealed channels feed nothing, enforced
              cryptographically — they hold ciphertext they cannot read. The
              connective tissue is the AGENT tier, which is a separate thing
              and is genuinely live. Saying so plainly matters more here than
              sounding impressive. */}
          <p className="text-sm text-slate-400">
            This graph is mirrored to fock.space as a private, end-to-end-encrypted overlay
            ({fock.synced_nodes ?? 0} nodes synced{fock.pending ? `, ${fock.pending} pending` : ""}).
            That mirror is <b className="text-slate-300">offsite encrypted backup, nothing more</b> —
            fock.space stores ciphertext it cannot read, so the overlay cannot be searched,
            traversed, or reasoned over by anything on their side.
          </p>
          <p className="text-sm text-slate-400">
            The firm&rsquo;s actual connection to the shared universe is the{" "}
            <b className="text-slate-300">agent tier</b>: Stonky holds a disclosed agent identity
            there and uses it to search canon before the board sits, anchor its theses to the
            canonical concepts they depend on, and publish resolved trades. That is a separate,
            un-encrypted, attributable channel — and it is the one that does the work.
          </p>
          <div className="flex flex-wrap gap-2">
            <button className="btn text-xs" disabled={busy === "v"}
              onClick={async () => { setBusy("v"); try { setVerify(await api.get<Verify>("/api/graph/fockspace/verify")); } finally { setBusy(null); } }}>
              {busy === "v" ? "reading…" : "Verify round-trip"}
            </button>
            {/* Confirm before revealing. This prints the firm's vault
                passphrase into the page — to its rightful owner, but a
                passphrase on screen is a passphrase in devtools, in a screen
                share, and in a shoulder-surf. The backend writes a
                credential_reveal row to the ledger on every call. */}
            <button className="btn text-xs" disabled={busy === "a"}
              onClick={async () => {
                if (access) { setAccess(null); return; }   // already on screen — just hide it
                if (!window.confirm(
                  "Show the firm's fock.space vault passphrase and visitor token on screen?\n\n" +
                  "These are master keys for the overlay. Anyone who can see this screen can " +
                  "copy them, and the reveal is recorded in the activity ledger.")) return;
                setBusy("a");
                try { setAccess(await api.get<Access>("/api/graph/fockspace/access")); }
                finally { setBusy(null); }
              }}>
              {access ? "Hide native access" : busy === "a" ? "reading…" : "Reveal native access…"}
            </button>
          </div>
          {verify && (
            <div className="text-xs text-up border-l-2 border-up pl-2">
              {verify.ok
                ? `✓ pulled back and decrypted ${verify.read_back_nodes} nodes / ${verify.read_back_edges} edges from fock.space — the overlay is live and intact there.`
                : `verify failed: ${verify.error}`}
            </div>
          )}
          {access?.visitor_token && (
            <div className="text-xs text-slate-400 border border-edge rounded-lg p-2 space-y-1">
              <div className="text-gold font-semibold">Native access (the firm&rsquo;s master keys — keep private)</div>
              <div>1. On <span className="font-mono">{access.atlas_url}</span>, set cookie <span className="font-mono">{access.visitor_cookie_name}</span> to:</div>
              <div className="font-mono break-all bg-ink rounded p-1 text-slate-300">{access.visitor_token}</div>
              <div>2. Open the personal layer and unlock with passphrase:</div>
              <div className="font-mono break-all bg-ink rounded p-1 text-slate-300">{access.vault_passphrase}</div>
              <div className="text-slate-500">Then the firm&rsquo;s overlay renders as your personal graph in fock.space. (A one-click hand-off needs a small addition to the fock.space app.)</div>
            </div>
          )}
          <p className="text-xs text-slate-500">
            The overlay is locked to the firm&rsquo;s vault; fock.space only ever holds ciphertext.
            Mirrors to <span className="font-mono">{fock.api_url}</span>.
          </p>
        </div>
      )}

      {summary && (
        <div className="flex flex-wrap gap-4 text-xs text-slate-400">
          <span>pending sync: {summary.sync.unsynced_nodes} nodes / {summary.sync.unsynced_edges} edges</span>
          <span>last mirror: {summary.sync.last_sync_at ? new Date(summary.sync.last_sync_at).toLocaleString() : "never"}</span>
          <span>platform access: {summary.platform_access.length === 0
            ? <span className="text-up">none</span>
            : <span className="text-gold">{summary.platform_access.length} view(s), latest {summary.platform_access[0].actor}</span>}</span>
        </div>
      )}

      {storyOrderId && <TradeStory exitOrderId={storyOrderId} onClose={() => setStoryOrderId(null)} />}
    </div>
  );
}

// A node's consequence set, in money. Walking the causal structure is only
// half of tracing a decision — a board session's directives mean nothing
// until you can see whether they made money, and that answer is a FAN, not a
// single trade. Same FIFO-matched P&L the Performance page and Trade Story
// report, so all three agree.
/** One side of the case file: the chain going up into what authorized this
 *  node, or down into what it caused. Falls back to the flat one-hop list if
 *  the ego call didn't land, so a failure here degrades rather than blanks. */
function EgoSide({ title, ego, rootId, direction, flat, onOpen }: {
  title: string;
  ego: Ego | null;
  rootId: string;
  direction: "up" | "down";
  flat: NodeRef[];
  onOpen: (id: string) => void;
}) {
  // Walk outward from the root one hop at a time, so the rendering shows a
  // CHAIN (charter ← session ← directive ← proposal ← this) rather than a
  // flat bag of neighbours at unknown distance.
  const byId = new Map((ego?.nodes ?? []).map((n) => [n.id, n]));
  const levels: { node: GNode & { hops: number }; rel: string; gap: number | null }[][] = [];
  if (ego) {
    let frontier = new Set([rootId]);
    const seen = new Set([rootId]);
    for (let depth = 0; depth < 2; depth++) {
      const here: { node: GNode & { hops: number }; rel: string; gap: number | null }[] = [];
      const next = new Set<string>();
      for (const e of ego.edges) {
        // "up" = walk toward causes: edges pointing INTO the frontier.
        const from = direction === "up" ? e.dst : e.src;
        const to = direction === "up" ? e.src : e.dst;
        if (!frontier.has(from) || seen.has(to)) continue;
        const n = byId.get(to);
        if (!n) continue;
        seen.add(to);
        next.add(to);
        here.push({ node: n, rel: e.rel, gap: e.gap_hours });
      }
      if (here.length === 0) break;
      levels.push(here);
      frontier = next;
    }
  }

  const empty = direction === "up" ? "— nothing authorized this" : "— nothing downstream yet";

  return (
    <div>
      <h3 className="label">{title}</h3>
      {levels.length === 0 && flat.length === 0 && (
        <div className="text-xs text-slate-600">{empty}</div>
      )}
      {/* Fallback: the original one-hop list, if ego didn't load. */}
      {levels.length === 0 && flat.length > 0 && flat.map((e, i) => (
        <button key={i} onClick={() => onOpen(e.id)}
          className="flex items-center gap-1.5 text-xs text-left w-full py-0.5 hover:text-gold">
          <ShapeSwatch type={e.type} size={8} />
          <span className="text-slate-500 font-mono">{direction === "up" ? `${e.rel} ←` : `→ ${e.rel}`}</span>
          <span className="text-slate-300 truncate">{e.label}</span>
        </button>
      ))}
      {levels.map((level, li) => (
        <div key={li} style={{ marginLeft: li * 10 }}
             className={li > 0 ? "border-l border-edge pl-2 mt-0.5" : ""}>
          {level.map((item, i) => (
            <button key={i} onClick={() => onOpen(item.node.id)}
              className="flex items-center gap-1.5 text-xs text-left w-full py-0.5 hover:text-gold"
              style={{ minHeight: 28 }}>
              <ShapeSwatch type={item.node.type} size={8} />
              <span className="text-slate-500 font-mono shrink-0">
                {direction === "up" ? `${item.rel} ←` : `→ ${item.rel}`}
              </span>
              <span className="text-slate-300 truncate">{item.node.label}</span>
              {/* The KAIZEN finding, made visible: a proposal citing a
                  directive written days earlier is the stale-directive bug
                  the firm already shipped a fix for. The gap was always in
                  the data; nothing ever showed it. */}
              {item.gap != null && item.gap >= 24 && (
                <span className="ml-auto shrink-0 text-gold" title="these two were created days apart">
                  ⚠ {Math.round(item.gap / 24)}d
                </span>
              )}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

function ImpactPanel({ impact, onOpenStory }: {
  impact?: Impact; onOpenStory: (k: string) => void;
}) {
  if (!impact || !impact.applicable) return null;
  const money = signedUsd;
  if (!impact.trades) {
    return (
      <div className="border border-edge rounded-lg p-3">
        <h3 className="label mb-0">Impact</h3>
        <div className="text-xs text-slate-500 font-sans">
          No closed trades traced to this node{impact.note ? ` (${impact.note})` : ""} — it may still be open, or never reached the market.
        </div>
      </div>
    );
  }
  return (
    <div className="border border-accent/40 rounded-lg p-3 space-y-2">
      <div>
        <h3 className="label mb-0">Impact — what this caused</h3>
        <div className="text-xs text-slate-500 font-sans">{impact.note}</div>
      </div>
      {/* Always rendered: a payload without a money type gets an explicit
          UNKNOWN, never a silent omission beside a dollar figure. */}
      <MoneyBadge money={readMoney(impact.money)} className="tracking-wider" />
      <div className="flex items-baseline gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-600">Net P&amp;L</div>
          <div className={`text-xl font-black font-mono ${impact.net_pnl >= 0 ? "text-up" : "text-down"}`}>
            {money(impact.net_pnl)}
          </div>
        </div>
        <div className="text-xs text-slate-400 font-sans">
          {impact.trades} trade{impact.trades > 1 ? "s" : ""} · {impact.wins}W / {impact.losses}L
        </div>
      </div>
      <ul className="space-y-0.5 max-h-56 overflow-y-auto">
        {impact.trades_list.map((t, i) => (
          <li key={i} className="flex items-baseline gap-2 font-mono text-xs">
            <span className="text-slate-400">{t.qty} {t.symbol}</span>
            <span className="text-slate-600">{t.side}</span>
            <span className="text-slate-600">{t.book}</span>
            <span className={`ml-auto ${t.pnl >= 0 ? "text-up" : "text-down"}`}>{money(t.pnl)}</span>
            {t.trade_key && (
              <button className="btn text-[10px]" onClick={() => onOpenStory(t.trade_key!)}>Story →</button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
